import { makeAutoObservable, runInAction } from "mobx"
import { type NestNotificationParams, type NestSubscriptionFilter, nestClient, requestNestMethod } from "../nest/client"
import { ulid } from "../utils/ulid"
import {
    type WildLarkTaskProposal,
    type WildLarkTaskProposalAuthor,
    type WildLarkTaskProposalResolveParams,
    type WildLarkTaskProposalStatus,
    requestWildLarkMethod,
} from "../wildlark/client"

export const TASK_PROPOSAL_NAMESPACE = "wildlark.proposals"
const TASK_PROPOSAL_KEY_PREFIX = "task."
const TASK_PROPOSAL_KEY_SUFFIX = ".v1"
const MAX_PROPOSALS = 100

export type TaskProposal = WildLarkTaskProposal

interface TaskProposalSubscriptionClient {
    retainSubscriptions(filters: NestSubscriptionFilter[]): Promise<() => void>
    on(method: "appData.changed", handler: (params: NestNotificationParams<"appData.changed"> | undefined) => void): () => void
}

type ProposalRequest = {
    get(params: { namespace: string; key: string; scope: { workspaceId: string } }): Promise<{ found: boolean; value?: unknown }>
    propose(params: { taskId: string; workspaceId: string; input: string; clientRequestId?: string }): Promise<{ proposal: WildLarkTaskProposal }>
    resolve(params: WildLarkTaskProposalResolveParams): Promise<{ proposal: WildLarkTaskProposal }>
}

interface TaskProposalStoreOptions {
    request?: ProposalRequest
    client?: TaskProposalSubscriptionClient
}

const defaultRequest: ProposalRequest = {
    get: (params) => requestNestMethod("appData.get", params),
    propose: (params) => requestWildLarkMethod("wildlark.task.propose", params),
    resolve: (params) => requestWildLarkMethod("wildlark.task.proposal.resolve", params),
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function optionalString(value: unknown): string | undefined {
    return typeof value === "string" && value.trim() ? value : undefined
}

function parseAuthor(value: unknown): WildLarkTaskProposalAuthor | null {
    if (!isRecord(value) || typeof value.principalId !== "string" || !value.principalId) return null
    return {
        principalId: value.principalId,
        kind: typeof value.kind === "string" ? value.kind : "person",
        displayName: typeof value.displayName === "string" && value.displayName ? value.displayName : "Someone",
        ...(optionalString(value.glyph) ? { glyph: value.glyph as string } : {}),
        ...(optionalString(value.color) ? { color: value.color as string } : {}),
    }
}

function parseStatus(value: unknown): WildLarkTaskProposalStatus | null {
    return value === "pending" || value === "approved" || value === "declined" || value === "withdrawn" ? value : null
}

export function parseTaskProposal(value: unknown): TaskProposal | null {
    if (!isRecord(value)) return null
    const status = parseStatus(value.status)
    const by = parseAuthor(value.by)
    if (typeof value.id !== "string" || !value.id || typeof value.input !== "string" || !status || !by || typeof value.createdAt !== "string") return null
    const resolvedBy = value.resolvedBy === undefined ? undefined : parseAuthor(value.resolvedBy)
    return {
        id: value.id,
        input: value.input,
        status,
        by,
        createdAt: value.createdAt,
        ...(optionalString(value.clientRequestId) ? { clientRequestId: value.clientRequestId as string } : {}),
        ...(optionalString(value.resolvedAt) ? { resolvedAt: value.resolvedAt as string } : {}),
        ...(resolvedBy ? { resolvedBy } : {}),
        ...(optionalString(value.turnId) ? { turnId: value.turnId as string } : {}),
        ...(optionalString(value.note) ? { note: value.note as string } : {}),
    }
}

export function parseTaskProposalRecord(value: unknown, taskId: string): TaskProposal[] {
    if (!isRecord(value) || value.v !== 1 || value.taskId !== taskId || !Array.isArray(value.proposals)) return []
    return value.proposals
        .slice(0, MAX_PROPOSALS)
        .map(parseTaskProposal)
        .filter((proposal): proposal is TaskProposal => proposal !== null)
}

export function proposalRecordKey(taskId: string): string {
    return `${TASK_PROPOSAL_KEY_PREFIX}${taskId}${TASK_PROPOSAL_KEY_SUFFIX}`
}

/**
 * Colleagues' suggestions for a task. The owner's client renders pending
 * proposals beside its queued turns; a guest sees its own. State is a bounded
 * workspace-scoped appData record written by the WildLark module, so the
 * store only point-reads the open task and follows change notifications.
 */
export class TaskProposalStore {
    readonly proposalsByTask = new Map<string, TaskProposal[]>()
    version = 0
    private readonly request: ProposalRequest
    private readonly client: TaskProposalSubscriptionClient
    private readonly watchers = new Map<string, { workspaceId: string; count: number; dispose: () => void }>()
    private loading = new Map<string, Promise<void>>()

    constructor(options: TaskProposalStoreOptions = {}) {
        this.request = options.request ?? defaultRequest
        this.client = options.client ?? nestClient
        makeAutoObservable(this, { proposalsByTask: false })
    }

    proposalsFor(taskId: string): TaskProposal[] {
        void this.version
        return this.proposalsByTask.get(taskId) ?? []
    }

    pendingFor(taskId: string): TaskProposal[] {
        return this.proposalsFor(taskId).filter((proposal) => proposal.status === "pending")
    }

    /** Follows one task while a view is open; returns the release function. */
    watch(taskId: string, workspaceId: string): () => void {
        const existing = this.watchers.get(taskId)
        if (existing) {
            existing.count += 1
            void this.load(taskId, workspaceId)
            return () => this.release(taskId)
        }
        let released = false
        let releaseSubscription: (() => void) | null = null
        const unsubscribe = this.client.on("appData.changed", (params) => {
            if (!params || params.namespace !== TASK_PROPOSAL_NAMESPACE || params.key !== proposalRecordKey(taskId)) return
            void this.load(taskId, workspaceId)
        })
        void this.client
            .retainSubscriptions([{ method: "appData.changed", appDataNamespace: TASK_PROPOSAL_NAMESPACE, appDataScope: { workspaceId } }])
            .then((dispose) => {
                if (released) dispose()
                else releaseSubscription = dispose
            })
            .catch((error) => console.warn("[TaskProposalStore] Could not subscribe to proposal changes", { taskId, error }))
        this.watchers.set(taskId, {
            workspaceId,
            count: 1,
            dispose: () => {
                released = true
                unsubscribe()
                releaseSubscription?.()
            },
        })
        void this.load(taskId, workspaceId)
        return () => this.release(taskId)
    }

    private release(taskId: string): void {
        const watcher = this.watchers.get(taskId)
        if (!watcher) return
        watcher.count -= 1
        if (watcher.count > 0) return
        watcher.dispose()
        this.watchers.delete(taskId)
    }

    async load(taskId: string, workspaceId: string): Promise<void> {
        const inFlight = this.loading.get(taskId)
        if (inFlight) return inFlight
        const load = (async () => {
            try {
                const result = await this.request.get({ namespace: TASK_PROPOSAL_NAMESPACE, key: proposalRecordKey(taskId), scope: { workspaceId } })
                const proposals = result.found ? parseTaskProposalRecord(result.value, taskId) : []
                runInAction(() => {
                    this.proposalsByTask.set(taskId, proposals)
                    this.version += 1
                })
            } catch (error) {
                console.warn("[TaskProposalStore] Could not load proposals", { taskId, error })
            }
        })()
        this.loading.set(taskId, load)
        try {
            await load
        } finally {
            if (this.loading.get(taskId) === load) this.loading.delete(taskId)
        }
    }

    async propose(taskId: string, workspaceId: string, input: string): Promise<TaskProposal> {
        const trimmed = input.trim()
        if (!trimmed) throw new Error("Write the suggestion first")
        const result = await this.request.propose({ taskId, workspaceId, input: trimmed, clientRequestId: `${taskId}:proposal:${ulid()}` })
        this.upsert(taskId, result.proposal)
        return result.proposal
    }

    async resolve(
        taskId: string,
        workspaceId: string,
        proposalId: string,
        outcome: "approved" | "declined" | "withdrawn",
        extra: { turnId?: string; note?: string } = {}
    ): Promise<TaskProposal> {
        const result = await this.request.resolve({ taskId, workspaceId, proposalId, outcome, ...extra })
        this.upsert(taskId, result.proposal)
        return result.proposal
    }

    private upsert(taskId: string, proposal: TaskProposal): void {
        runInAction(() => {
            const current = this.proposalsByTask.get(taskId) ?? []
            const next = current.some((existing) => existing.id === proposal.id)
                ? current.map((existing) => (existing.id === proposal.id ? proposal : existing))
                : [...current, proposal]
            this.proposalsByTask.set(taskId, next)
            this.version += 1
        })
    }

    dispose(): void {
        for (const watcher of this.watchers.values()) watcher.dispose()
        this.watchers.clear()
        this.proposalsByTask.clear()
    }
}
